import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { inboxConversationService } from '@/services/conversation.service';
import type { InboxLocation } from '@/types/conversation';

export const INBOX_LOCATION_STORAGE_KEY = 'box-os-inbox-location';

// Module-level store so every hook instance (InboxPage, useInboxCount inside
// useModuleConfig) observes the same selected location (D-04, per browser).
const listeners = new Set<() => void>();

function readStored(): string | null {
  try {
    return window.localStorage.getItem('box-os-inbox-location');
  } catch {
    return null;
  }
}

function writeStored(id: string | null): void {
  try {
    if (id) window.localStorage.setItem('box-os-inbox-location', id);
    else window.localStorage.removeItem('box-os-inbox-location');
  } catch {
    // storage unavailable: selection stays in-memory only for this tick
  }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (e: StorageEvent) => {
    if (e.key === null || e.key === INBOX_LOCATION_STORAGE_KEY) listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

export interface UseInboxLocationResult {
  locations: InboxLocation[];
  locationId: string | null;
  storedLocationId: string | null;
  setLocationId: (id: string) => void;
  showSwitcher: boolean;
  isLoading: boolean;
  error: unknown;
}

export function useInboxLocation({
  enabled = true,
}: { enabled?: boolean } = {}): UseInboxLocationResult {
  const storedLocationId = useSyncExternalStore(subscribe, readStored, () => null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['inbox', 'locations'],
    queryFn: () => inboxConversationService.getLocations(),
    enabled,
    staleTime: 5 * 60_000,
  });
  const locations = data ?? [];

  // T-385-14: stored id is only trusted when the server returned it.
  const resolved =
    storedLocationId && locations.some((l) => l.id === storedLocationId)
      ? storedLocationId
      : (locations[0]?.id ?? null);

  useEffect(() => {
    if (resolved && resolved !== storedLocationId) writeStored(resolved);
  }, [resolved, storedLocationId]);

  const setLocationId = useCallback((id: string) => writeStored(id), []);

  return {
    locations,
    locationId: resolved,
    storedLocationId,
    setLocationId,
    showSwitcher: locations.length > 1,
    isLoading,
    error,
  };
}
